// @ts-nocheck
import { useState } from 'react';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import CollapsibleSection from './CollapsibleSection';
import HeadingInfo from './HeadingInfo';
import { AI_AGENT_FIELD_LIMITS } from '@/utilities/constant';
import { getAiAgentDescriptionCharLimit } from '@/utilities/aiAgentAcl';

const { COMPLETION_TYPE_MAX_ITEMS, COMPLETION_TYPE_NAME_MAX } = AI_AGENT_FIELD_LIMITS;

const SNAKE_RE = /^[a-z0-9_]+$/;

const errClass = (hasErr: boolean) =>
  hasErr ? 'border-destructive focus-visible:ring-destructive' : '';

const limitToast = (id: string, msg: string) => toast.error(msg, { id });

export default function CompletionRulesSection({ rules, onChange }: any) {
  const list = rules || [];

  const [openId, setOpenId] = useState<string | null>(
    () => list.find((r: any) => !r.type?.trim() && !r.description?.trim())?.id ?? null,
  );

  const descLimit = getAiAgentDescriptionCharLimit();
  const [attemptedId, setAttemptedId] = useState<string | null>(null);

  const getRuleErrors = (rule: any, showRequired: boolean) => {
    const e: any = {};
    const t = (rule.type || '').trim();
    if (!t) {
      if (showRequired) e.type = 'Type of completion is required';
    } else if (!SNAKE_RE.test(t)) {
      e.type = 'Must be snake_case (lowercase letters, numbers and underscores only)';
    } else if (t.length > COMPLETION_TYPE_NAME_MAX) {
      e.type = `Type of completion can be max ${COMPLETION_TYPE_NAME_MAX} characters`;
    } else if (list.some((o: any) => o.id !== rule.id && (o.type || '').trim() === t)) {
      e.type = 'Type of completion must be unique';
    }
    if (!rule.description?.trim()) {
      if (showRequired) e.description = 'Completion description is required';
    } else if (rule.description.length > descLimit) {
      e.description = `Description can be max ${descLimit} characters`;
    }
    return e;
  };

  const addRule = () => {
    if (list.length >= COMPLETION_TYPE_MAX_ITEMS) {
      toast.error(`You can add up to ${COMPLETION_TYPE_MAX_ITEMS} completion types`);
      return;
    }
    const current = list.find((r: any) => r.id === openId);
    if (current && !current.type?.trim() && !current.description?.trim()) {
      setAttemptedId(current.id);
      toast.error('Fill the current rule and click the tick first');
      return;
    }
    const item = { id: crypto.randomUUID(), type: '', description: '' };
    onChange([...list, item]);
    setOpenId(item.id);
  };

  const updateRule = (id: string, patch: any) => {
    onChange(list.map((r: any) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRule = (id: string) => {
    onChange(list.filter((r: any) => r.id !== id));
    if (openId === id) setOpenId(null);
  };

  // Tick: validate, then move the rule into the listing.
  const commitRule = (rule: any) => {
    const errs = getRuleErrors(rule, true);
    const first = errs.type || errs.description;
    if (first) {
      setAttemptedId(rule.id);
      toast.error(first);
      return;
    }
    setAttemptedId(null);
    setOpenId(null);
  };


  const handleTypeChange = (id: string, raw: string) => {
    const spaced = raw.toLowerCase().replace(/\s+/g, '_');
    const cleaned = spaced.replace(/[^a-z0-9_]/g, '');
    if (cleaned !== spaced) {
      limitToast('cr-type-chars', 'Type of completion must be snake_case (lowercase letters, numbers and underscores only)');
    }
    let value = cleaned;
    if (value.length > COMPLETION_TYPE_NAME_MAX) {
      limitToast('cr-type-max', `Type of completion can be max ${COMPLETION_TYPE_NAME_MAX} characters`);
      value = value.slice(0, COMPLETION_TYPE_NAME_MAX);
    }
    updateRule(id, { type: value });
  };

  const handleDescChange = (id: string, raw: string) => {
    let value = raw;
    if (value.length > descLimit) {
      limitToast('cr-desc-max', `Description can be max ${descLimit} characters`);
      value = value.slice(0, descLimit);
    }
    updateRule(id, { description: value });
  };

  return (
    <CollapsibleSection title="Completion Rules" required info="Define the conditions that mark this conversation as finished, e.g. issue resolved or order placed. The AI checks these rules to decide when to end or hand off the call.">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-foreground">
            Completion Rules
            <span className="ml-1.5 text-xs font-normal text-muted-foreground">
              ({list.length}/{COMPLETION_TYPE_MAX_ITEMS})
            </span>
          </p>
          <Button size="sm" variant="outline" onClick={addRule} disabled={list.length >= COMPLETION_TYPE_MAX_ITEMS}>
            <Plus className="h-4 w-4" />
            Add
          </Button>
        </div>

        {list.length === 0 && (
          <p className="py-4 text-center text-sm text-muted-foreground">No completion rules added yet.</p>
        )}

        {list.map((rule: any, index: number) => {
          const errs = getRuleErrors(rule, attemptedId === rule.id);
          return rule.id === openId ? (
            <div key={rule.id} className="space-y-3 rounded-md border border-border p-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-foreground">Rule {index + 1}</p>
                <span className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-emerald-600 hover:text-emerald-700"
                    onClick={() => commitRule(rule)}
                    title="Done"
                    aria-label="Done"
                  >
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-destructive hover:text-destructive"
                    onClick={() => removeRule(rule.id)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </span>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>
                    Type of Completion <span className="text-destructive">*</span>
                  </Label>
                  <HeadingInfo text={`A short identifier for this reason a call can end. Example: caller_requests_human. Max ${COMPLETION_TYPE_NAME_MAX} chars; snake_case.`} />
                </div>
                <Input
                  placeholder="e.g. card_selected"
                  value={rule.type}
                  onChange={(e) => handleTypeChange(rule.id, e.target.value)}
                  className={errClass(!!errs.type || (rule.type || '').length >= COMPLETION_TYPE_NAME_MAX)}
                  autoFocus
                />
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-destructive">
                    {errs.type ||
                      ((rule.type || '').length >= COMPLETION_TYPE_NAME_MAX
                        ? `Max ${COMPLETION_TYPE_NAME_MAX} characters reached`
                        : '')}
                  </p>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {(rule.type || '').length}/{COMPLETION_TYPE_NAME_MAX}
                  </span>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-1.5">
                  <Label>
                    Description <span className="text-destructive">*</span>
                  </Label>
                  <HeadingInfo text="Explains when this completion type applies, so the agent knows when to report it." />
                </div>
                <Textarea
                  rows={2}
                  placeholder="e.g. Customer confirmed which card they want and agreed to close the chat."
                  value={rule.description}
                  onChange={(e) => handleDescChange(rule.id, e.target.value)}
                  className={errClass(!!errs.description || (rule.description || '').length >= descLimit)}
                />
                <div className="flex items-start justify-between gap-2">
                  <p className="text-xs text-destructive">
                    {errs.description || ((rule.description || '').length >= descLimit ? `Max ${descLimit} characters reached` : '')}
                  </p>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {(rule.description || '').length}/{descLimit}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div
              key={rule.id}
              className="flex items-center justify-between rounded-md border px-3 py-2"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-foreground">
                  {rule.type || `Rule ${index + 1}`}
                </p>
                {rule.description && (
                  <p className="truncate text-xs text-muted-foreground">{rule.description}</p>
                )}
              </div>
              <span className="flex shrink-0 items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:text-primary"
                  onClick={() => setOpenId(rule.id)}
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-destructive hover:text-destructive"
                  onClick={() => removeRule(rule.id)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </span>
            </div>
          );
        })}
      </div>
    </CollapsibleSection>
  );
}
